<template>
  <PostboxTableFrame id="postbox-table-frame">
    <template #postboxTableHead>
      <tr>
        <th class="postbox__date" scope="col">
          {{ $t('postbox.headDate') }}
        </th>
        <th class="postbox__message" scope="col">
          {{ $t('postbox.headSubject') }}
        </th>
        <th
          v-if="accountDocumentReadPermission || hasMailDueDate || isHelpdesk"
          class="postbox__action"
          scope="col"
        ></th>
      </tr>
    </template>
    <template #postboxTableBody>
      <tr
        v-for="(document, index) of documentsFiltered"
        :class="{
          highlighted: document.readStatus === 'unread',
          disableHover: !accountDocumentReadPermission
        }"
        @click="
          documentIsInFuture(document) ? openModal() : downloadFileMethod(document, true)
        "
        class="postbox__row"
        :key="index"
        @keydown.enter.prevent="onRowKeydown($event)"
        @keydown.space.prevent="onRowKeydown($event)"
        @keyup.enter.prevent="onRowKeyup(document)"
        @keyup.space.prevent="onRowKeyup(document)"
        data-track-id="downloadFile"
      >
        <td class="postbox__date">
          <span v-if="!documentIsInFuture(document)">{{ document.creationDate }}</span>
          <span
            v-if="document.readStatus === 'unread'"
            class="status status--deleted status--deleted--postfach"
          ></span>
        </td>
        <td
          :class="{
            'postbox__message--has-actions':
              accountDocumentReadPermission || documentIsInFuture(document),
            'disabled-color': documentIsInFuture(document)
          }"
          class="postbox__message"
        >
          <div class="content">
            {{ $t('postbox.line.' + document.line) + ' / ' + document.kundenVSNr }}
          </div>
          <div class="secondary">{{ document.documentName }}</div>
        </td>
        <td
          v-if="accountDocumentReadPermission && !documentIsInFuture(document)"
          class="postbox__action"
        >
          <a
            :id="`postbox_download-button_${document.documentId}`"
            class="td-action"
            tabindex="0"
            :aria-label="$t('postbox.downloadButtonAltText')"
          >
            <span class="td-action icon-download"></span>
          </a>
        </td>
        <td v-if="documentIsInFuture(document)" class="postbox__action">
          <div
            :id="`postbox_info-button_${document.documentId}`"
            class="td-action"
            style="pointer-events: all"
          >
            <span class="td-action icon-vorteile"></span>
          </div>
        </td>
      </tr>
    </template>
  </PostboxTableFrame>
  <Modal
    v-if="showModal"
    :showModal="showModal"
    :centeredText="false"
    :maxWidth="800"
    :showCloseIcon="true"
    @closeModal="closeModal"
  >
    <template v-slot:content>
      <div>Der Versand des Dokuments liegt in der Zukunft.</div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { downloadFile } from './downloadFile.sonarExclude'
import { type Document } from '@/types/postbox/postbox-service'
import { hasRole } from '@/utils/KeycloakUtils'
import dayjs from 'dayjs'
import PostboxTableFrame from '@/components/postbox/PostboxTableFrame.vue'
import { Modal } from 'de-zurich-ui-library'

const { t } = useI18n()

const props = defineProps({
  documentsFiltered: {
    type: Array as () => Document[],
    required: true
  },
  accountDocumentReadPermission: {
    type: Boolean,
    required: true
  }
})

const showModal = ref(false)

const keyPressed = ref(false)

const isHelpdesk = computed(() => hasRole('helpdesk'))

const documentIsInFuture = (document: Document) => {
  if (document.mailDueDate === null) return false
  const documentSendDate = dayjs(document.mailDueDate, 'DD.MM.YYYY', true)
  const now = dayjs()
  return documentSendDate.isAfter(now)
}

const hasMailDueDate = computed(() => {
  return props.documentsFiltered.some((document) => documentIsInFuture(document))
})

const openModal = () => {
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}

const downloadFileMethod = async (document: Document, openInNewTab: boolean) => {
  if (!props.accountDocumentReadPermission) return
  downloadFile(document, openInNewTab)
}

const onRowKeydown = (_event: KeyboardEvent) => {
  if (keyPressed.value) {
    return
  }
  keyPressed.value = true
}

const onRowKeyup = (document: Document) => {
  keyPressed.value = false

  if (documentIsInFuture(document)) {
    openModal()
  } else {
    downloadFileMethod(document, true)
  }
}
</script>

<style lang="scss" scoped>
/* Hide scrollbar for IE, Edge and Firefox */

.status--deleted--postfach {
  margin-right: -4px !important;
  margin-top: 7px;
}

.postbox__row.disableHover {
  pointer-events: none;
}

@media (max-width: 599.98px) {
  .postbox__date {
    width: calc(var(--grid-gutter-width) * 9) !important;
  }

  .status--deleted--postfach {
    margin-left: 8px;
  }
}

@media (min-width: 600px) {
  .postbox__date > span {
    display: block;
  }
}

:deep() .postbox__row .postbox__action {
  text-align: center !important;
}

:deep() .postbox__row .postbox__action ~ .postbox__action {
  margin-bottom: 19px;
}

@media (hover) {
  .postbox__row:hover {
    color: var(--postbox-row-hover-color);
    cursor: pointer;

    .icon-download {
      color: var(--postbox-row-hover-color) !important;
    }
  }
}

.data-table th {
  font-weight: 600;
}

@media (min-width: 600px) {
  .status--deleted--postfach {
    float: right;
  }
}

.td-action:focus-visible ::before {
  outline: 2px solid var(--primary-color-0) !important;
  outline-offset: 8px;
  border-radius: 2px;
}
</style>
